import { takeLatest, delay, eventChannel } from 'redux-saga'
import {
	select,
	take,
	call,
	put,
	cancelled,
	race,
} from 'redux-saga/effects'
import config from 'config'
import activityTypes from 'helpers/enums/activityTypes'
import actionTypes from 'redux/actions/actionTypes'
import activitiesActions from 'redux/actions/activitiesActions'
import socketConnect from 'sync/helpers/socketConnection'
import interactiveVideoSessionInitializationSelector from 'redux/selectors/interactive_videos/session_initialization'

const actionsToActivityTypes = {
	[actionTypes.ACTIVITY_MCQ_MOUNTED]: activityTypes.MULTIPLE_CHOICE_QUESTION,
	[actionTypes.ACTIVITY_MCQ_STUDENT_MOUNTED]: activityTypes.MULTIPLE_CHOICE_QUESTION,
	[actionTypes.ACTIVITY_OEQ_MOUNTED]: activityTypes.OPEN_ENDED_QUESTION,
	[actionTypes.ACTIVITY_OEQ_STUDENT_MOUNTED]: activityTypes.OPEN_ENDED_QUESTION,
}

const activityTypesToActionGenerators = {
	[activityTypes.MULTIPLE_CHOICE_QUESTION]: activitiesActions.mcqStatusFetched,
	[activityTypes.OPEN_ENDED_QUESTION]: activitiesActions.oeqStatusFetched,
}

function *initializeActivityConnection(action) {
	yield race([
		call(connect, action),
		call(listenToActivityEnd, action.questionUid),
	])
}

function *connect(action) {
	const interactiveVideoSessionData = yield select(interactiveVideoSessionInitializationSelector)
	while (true) {
		const socket = yield call(startSocketConnection, interactiveVideoSessionData, action)
		yield call(subscribeToSocket, socket, actionsToActivityTypes[action.type])
		yield call(socket.disconnect)
		yield call(delay, 2000)
	}
}

function *listenToActivityEnd(activityUid) {
	while (true) {
		const action = yield take([
			actionTypes.INTERACTIVE_VIDEO_MCQ_COMPONENT_UNMOUNTED,
			actionTypes.ACTIVITY_MCQ_UNMOUNTED,
			actionTypes.INTERACTIVE_VIDEO_OEQ_COMPONENT_UNMOUNTED,
			actionTypes.ACTIVITY_OEQ_UNMOUNTED,
		])
		if (action.questionUid === activityUid) {
			break
		}
	}
}

function completeConnection(socket, videoSessionData, action) {
	return new Promise(function(resolve) {

		const removeListeners = () => {
			socket.off('disconnected', disconnected)
		}

		const disconnected = () => {
			removeListeners()
			resolve(false)
		}
		socket.on('disconnect', disconnected)
		socket.emit('join', {
			mediaSessionUid: videoSessionData.uid,
			activityUid: action.questionUid,
			jwt: videoSessionData.jwt,
		}).then( () => {
			resolve(true)
		})
	})
}

function *startSocketConnection(videoSessionData, action) {
	let socket
	try {
		while (true) {
			socket = socketConnect(
				config.activities_media_socket,
				['websocket', 'polling']
			)
			yield call(socket.connect)
			const isConnected = yield call(
				completeConnection,
				socket,
				videoSessionData,
				action,
			)
			if (isConnected === true) {
				return socket
			} else {
				yield call(delay, 10000)
			}
		}
	} catch (err) {
		socket.disconnect()
	} finally {
		if (yield cancelled()) {
			socket.disconnect()
		}
	}
}

function *subscribeToSocket(socket, activityType) {
	function subscribe(socket) {
		return eventChannel(emit => {
			socket.on('status', message => {
				emit({type: 'status', message})
			})
			socket.on('disconnect', () => {
				emit({type: 'disconnect'})
			})
			return () => {}
		})
	}
	function *getState() {
		try {
			const {state} = yield race(
				{
					state: call(socket.emit, 'getState'),
					timeout: call(delay, 5000),
				})
			if (state) {
				yield put(
					activityTypesToActionGenerators[activityType](
						state,
					)
				)
			}
		} catch (err) {
			//
		}
	}
	try {
		const channel = yield call(subscribe, socket)
		yield call(getState)
		let isConnected = true
		while (isConnected) {
			const channel_message = yield take(channel)
			if (channel_message.type === 'disconnect') {
				isConnected = false
			} else if (channel_message.type === 'status') {
				yield put(
					activityTypesToActionGenerators[activityType](
						channel_message.message,
					)
				)
			}
		}
	} catch (err) {
		socket.disconnect()
	} finally {
		if (yield cancelled()) {
			socket.disconnect()
		}
	}

}

export default [
	takeLatest([
		actionTypes.ACTIVITY_MCQ_MOUNTED,
		actionTypes.ACTIVITY_MCQ_STUDENT_MOUNTED,
		actionTypes.ACTIVITY_OEQ_MOUNTED,
		actionTypes.ACTIVITY_OEQ_STUDENT_MOUNTED,
	],
	initializeActivityConnection),
]

export {
	initializeActivityConnection,
}
